import { ConfigContext } from "App";
import { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Badge, Card, CardBody, CardHeader, Col, Row } from "reactstrap";
import { useReportScope } from "hooks/useReportScope";
import { buildReportUrl } from "helpers/reports_url_helper";
import { Column } from "common/data/data_types";
import {
    AdjustmentType,
    InventoryAdjustmentByType,
    InventoryAdjustmentReportKpis,
    InventoryAdjustmentReportRecord,
} from "common/data_interfaces";
import LoadingAnimation from "Components/Common/Shared/LoadingAnimation";
import AlertMessage from "Components/Common/Shared/AlertMesagge";
import ReportPageLayout from "Components/Common/Shared/ReportPageLayout";
import StatKpiCard from "Components/Common/Graphics/StatKpiCard";
import CustomTable from "Components/Common/Tables/CustomTable";
import BasicBarChart from "Components/Common/Graphics/BasicBarChart";

const formatDate = (iso: string): string => {
    if (!iso) return "—";
    return new Date(iso).toLocaleDateString("es-MX", { day: "2-digit", month: "2-digit", year: "numeric" });
};

const InventoryAdjustmentsReport = () => {
    const { t } = useTranslation();
    document.title = `${t("reports.invAdjustments.title")} | ${t("reports.title")}`;

    const configContext = useContext(ConfigContext);
    const { isGlobal, farmId, scopeKey } = useReportScope();

    const today = new Date();
    const monthStart = new Date(today.getFullYear(), today.getMonth(), 1);
    const monthEnd = new Date(today.getFullYear(), today.getMonth() + 1, 0);
    const [startDate, setStartDate] = useState(monthStart.toISOString().split("T")[0]);
    const [endDate, setEndDate] = useState(monthEnd.toISOString().split("T")[0]);

    const [loading, setLoading] = useState(false);
    const [alertConfig, setAlertConfig] = useState({ visible: false, color: "", message: "" });

    const [kpis, setKpis] = useState<InventoryAdjustmentReportKpis>({
        totalAdjustments: 0,
        totalIncreases: 0,
        totalDecreases: 0,
        totalFinancialImpact: 0,
        activeCount: 0,
        revertedCount: 0,
    });
    const [adjustments, setAdjustments] = useState<InventoryAdjustmentReportRecord[]>([]);
    const [byType, setByType] = useState<InventoryAdjustmentByType[]>([]);

    const typeLabels: Record<AdjustmentType, { label: string; color: string }> = {
        shrinkage: { label: t("inventoryAdjustments.adjustmentType.shrinkage"), color: "warning" },
        breakage: { label: t("inventoryAdjustments.adjustmentType.breakage"), color: "danger" },
        expiration: { label: t("inventoryAdjustments.adjustmentType.expiration"), color: "secondary" },
        count_correction: { label: t("inventoryAdjustments.adjustmentType.count_correction"), color: "info" },
        other: { label: t("inventoryAdjustments.adjustmentType.other"), color: "dark" },
    };

    const directionColors: Record<string, string> = {
        increase: "success",
        decrease: "danger",
    };

    const fetchData = async () => {
        if (!configContext) return;
        setLoading(true);
        try {
            const url = buildReportUrl({
                apiUrl: configContext.apiUrl,
                basePath: "reports/inventory/adjustments",
                isGlobal,
                farmId,
                query: { start_date: startDate, end_date: endDate },
            });
            const res = await configContext.axiosHelper.get(url);
            const data = res.data.data;
            setKpis(data.kpis);
            setAdjustments(data.adjustments || []);
            setByType(data.byType || []);
        } catch {
            setAlertConfig({ visible: true, color: "danger", message: t("reports.error.loadData") });
        } finally {
            setLoading(false);
        }
    };

    const handleGeneratePdf = async (pdfStart: string, pdfEnd: string): Promise<string> => {
        if (!configContext) throw new Error("No config");
        const url = buildReportUrl({
            apiUrl: configContext.apiUrl,
            basePath: "reports/inventory/adjustments",
            isGlobal,
            farmId,
            variant: "pdf",
            query: { start_date: pdfStart, end_date: pdfEnd, orientation: "landscape", format: "A4" },
        });
        const response = await configContext.axiosHelper.getBlob(url);
        const pdfBlob = new Blob([response.data], { type: "application/pdf" });
        return window.URL.createObjectURL(pdfBlob);
    };

    useEffect(() => {
        fetchData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [startDate, endDate, scopeKey]);

    const adjustmentColumns: Column<InventoryAdjustmentReportRecord>[] = [
        {
            header: t("reports.col.date"),
            accessor: "date",
            type: "text",
            isFilterable: true,
            render: (v: string) => formatDate(v),
        },
        {
            header: t("reports.col.warehouse"),
            accessor: "warehouse",
            type: "text",
            isFilterable: true,
        },
        {
            header: t("inventoryAdjustments.table.col.adjustmentType"),
            accessor: "adjustmentType",
            type: "text",
            isFilterable: true,
            render: (v: AdjustmentType) => {
                const lbl = typeLabels[v] || { label: v, color: "secondary" };
                return <Badge color={lbl.color}>{lbl.label}</Badge>;
            },
        },
        {
            header: t("inventoryAdjustments.table.col.direction"),
            accessor: "direction",
            type: "text",
            render: (v: string | null) => {
                if (!v) return <span className="text-muted">—</span>;
                return (
                    <Badge color={directionColors[v] || "secondary"}>
                        {t(`inventoryAdjustments.direction.${v}`)}
                    </Badge>
                );
            },
        },
        {
            header: t("reports.invAdjustments.col.products"),
            accessor: "products",
            type: "text",
            render: (_: any, row: InventoryAdjustmentReportRecord) => (
                <span className="text-muted small">
                    {row.productsCount}{" "}
                    {row.productsCount === 1
                        ? t("reports.invAdjustments.product")
                        : t("reports.invAdjustments.products")}
                </span>
            ),
        },
        {
            header: t("inventoryAdjustments.table.col.financialImpact"),
            accessor: "totalFinancialImpact",
            type: "text",
            bgColor: "#FFF3E0",
            render: (v: number) => <span className="fw-semibold">${v.toFixed(2)}</span>,
        },
        {
            header: t("reports.col.reason"),
            accessor: "reason",
            type: "text",
        },
        {
            header: t("reports.col.status"),
            accessor: "status",
            type: "text",
            render: (v: string) => (
                <Badge color={v === "active" ? "success" : "secondary"}>
                    {t(`inventoryAdjustments.status.${v}`)}
                </Badge>
            ),
        },
        {
            header: t("reports.col.user"),
            accessor: "createdBy",
            type: "text",
        },
    ];

    const byTypeColumns: Column<InventoryAdjustmentByType>[] = [
        {
            header: t("reports.col.type"),
            accessor: "type",
            type: "text",
            render: (v: AdjustmentType) => {
                const lbl = typeLabels[v] || { label: v, color: "secondary" };
                return <Badge color={lbl.color}>{lbl.label}</Badge>;
            },
        },
        {
            header: t("reports.invAdjustments.col.count"),
            accessor: "count",
            type: "text",
            render: (v: number) => <span className="fw-semibold">{v}</span>,
        },
        {
            header: t("reports.invAdjustments.col.totalImpact"),
            accessor: "totalImpact",
            type: "text",
            bgColor: "#FFF3E0",
            render: (v: number) => <span className="fw-semibold">${v.toFixed(2)}</span>,
        },
    ];

    const barData = byType.map((b) => ({
        tipo: typeLabels[b.type]?.label || b.type,
        Cantidad: b.count,
        Impacto: b.totalImpact,
    }));

    if (loading) return <LoadingAnimation />;

    return (
        <ReportPageLayout
            title={t("reports.invAdjustments.title")}
            pageTitle={t("reports.inventory")}
            onGeneratePdf={handleGeneratePdf}
            pdfTitle={t("reports.invAdjustments.pdfTitle")}
            startDate={startDate}
            endDate={endDate}
            onDateChange={(s, e) => { setStartDate(s); setEndDate(e); }}
        >
            <Row className="g-3 mb-3">
                <Col xl={2} md={4} sm={6}>
                    <StatKpiCard
                        title={t("reports.invAdjustments.kpi.totalAdjustments")}
                        value={kpis.totalAdjustments}
                        icon={<i className="ri-file-list-3-line fs-4 text-primary" />}
                        animateValue
                    />
                </Col>
                <Col xl={2} md={4} sm={6}>
                    <StatKpiCard
                        title={t("reports.invAdjustments.kpi.totalIncreases")}
                        value={kpis.totalIncreases}
                        icon={<i className="ri-arrow-up-circle-line fs-4 text-success" />}
                        animateValue
                        iconBgColor="#E8F5E9"
                    />
                </Col>
                <Col xl={2} md={4} sm={6}>
                    <StatKpiCard
                        title={t("reports.invAdjustments.kpi.totalDecreases")}
                        value={kpis.totalDecreases}
                        icon={<i className="ri-arrow-down-circle-line fs-4 text-danger" />}
                        animateValue
                        iconBgColor="#FFEBEE"
                    />
                </Col>
                <Col xl={2} md={4} sm={6}>
                    <StatKpiCard
                        title={t("reports.invAdjustments.kpi.financialImpact")}
                        value={kpis.totalFinancialImpact}
                        prefix="$"
                        decimals={2}
                        icon={<i className="ri-money-dollar-circle-line fs-4 text-warning" />}
                        animateValue
                        iconBgColor="#FFF8E1"
                    />
                </Col>
                <Col xl={2} md={4} sm={6}>
                    <StatKpiCard
                        title={t("reports.invAdjustments.kpi.activeCount")}
                        value={kpis.activeCount}
                        icon={<i className="ri-checkbox-circle-line fs-4 text-success" />}
                        animateValue
                        iconBgColor="#E8F5E9"
                    />
                </Col>
                <Col xl={2} md={4} sm={6}>
                    <StatKpiCard
                        title={t("reports.invAdjustments.kpi.revertedCount")}
                        value={kpis.revertedCount}
                        icon={<i className="ri-arrow-go-back-line fs-4 text-secondary" />}
                        animateValue
                        iconBgColor="#F5F5F5"
                    />
                </Col>
            </Row>

            {barData.length > 0 && (
                <Row className="g-3 mb-3">
                    <Col xl={12}>
                        <BasicBarChart
                            title={t("reports.invAdjustments.chart.byType")}
                            data={barData}
                            indexBy="tipo"
                            keys={["Cantidad"]}
                            xLegend={t("reports.axis.type")}
                            yLegend={t("reports.axis.quantity")}
                            height={250}
                        />
                    </Col>
                </Row>
            )}

            <Row className="g-3 mb-3">
                <Col xl={4}>
                    <Card className="h-100">
                        <CardHeader>
                            <h6 className="mb-0">{t("reports.invAdjustments.tab.byType")}</h6>
                        </CardHeader>
                        <CardBody>
                            <CustomTable columns={byTypeColumns} data={byType} rowsPerPage={10} />
                        </CardBody>
                    </Card>
                </Col>
                <Col xl={8}>
                    <Card className="h-100">
                        <CardHeader>
                            <h6 className="mb-0">
                                {t("reports.invAdjustments.tab.detail")} ({adjustments.length})
                            </h6>
                        </CardHeader>
                        <CardBody>
                            <CustomTable
                                columns={adjustmentColumns}
                                data={adjustments}
                                showSearchAndFilter
                                rowsPerPage={15}
                            />
                        </CardBody>
                    </Card>
                </Col>
            </Row>

            <AlertMessage
                color={alertConfig.color}
                message={alertConfig.message}
                visible={alertConfig.visible}
                onClose={() => setAlertConfig(prev => ({ ...prev, visible: false }))}
            />
        </ReportPageLayout>
    );
};

export default InventoryAdjustmentsReport;
